import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { formatReportDate } from '../../reports/formatting';
import { peopleGateway } from '../../people';
import type { DepartmentOption } from '../../people/types';
import { ArchiveRoleDialog } from '../components/ArchiveRoleDialog';
import { AssignEmployeesDialog } from '../components/AssignEmployeesDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DuplicateRoleDialog } from '../components/DuplicateRoleDialog';
import { EmploymentGuidance, Facts, QualificationsPanel, ResponsibilitiesPanel } from '../components/JobDescription';
import { CompensationTab } from '../components/CompensationTab';
import { AssignedEmployeesTab, AuditHistoryTab, VersionHistoryTab } from '../components/RoleDetailTabs';
import { RoleStatusBadge } from '../components/RoleStatusBadge';
import { RoleWizardDialog, type WizardOutcome } from '../components/RoleWizardDialog';
import { Tabs, type TabDef } from '../components/Tabs';
import { jobRoleGateway } from '../index';
import { CONCURRENCY_CONFLICT_MESSAGE, isConcurrencyConflict, isForbidden, isNotFound, jobRoleErrorMessage } from '../jobRoleErrors';
import { JobRoleStatus, normalizeJobRoleContent, type JobRoleDetail } from '../types';
import '../job-roles.css';

const TABS: TabDef[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'responsibilities', label: 'Responsibilities' },
  { key: 'qualifications', label: 'Qualifications' },
  { key: 'employees', label: 'Assigned Employees' },
  { key: 'versions', label: 'Version History' },
  { key: 'audit', label: 'Audit History' },
  { key: 'compensation', label: 'Compensation' },
];

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; role: JobRoleDetail }
  | { kind: 'forbidden' }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

type Dialog = 'edit' | 'duplicate' | 'archive' | 'assign' | 'activate' | null;

/**
 * One job role: Overview, Responsibilities, Qualifications, Assigned Employees, Version History, Audit History and Compensation
 * (the employer pay band and the market benchmark, kept apart; no employee's pay). Only a Draft is editable; an Active role changes through
 * Create New Version, then Edit, then Activate. The FLSA and overtime fields are shown as the employer's own classification with a
 * review warning. Every permission decision is the server's.
 */
export function RoleDetailPage() {
  const { roleId = '' } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get('tab') ?? 'overview';
  const tab = TABS.some((t) => t.key === requestedTab) ? requestedTab : 'overview';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [readOnly, setReadOnly] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let cancelled = false;
    peopleGateway.listDepartments().then((d) => { if (!cancelled) setDepartments(d.filter((x) => x.isActive)); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState((prev) => (prev.kind === 'ready' && prev.role.id === roleId ? prev : { kind: 'loading' }));
    jobRoleGateway
      .getRole(roleId, controller.signal)
      .then((role) => { if (!controller.signal.aborted) setState({ kind: 'ready', role }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isForbidden(err)) setState({ kind: 'forbidden' });
        else if (isNotFound(err)) setState({ kind: 'notfound' });
        else setState({ kind: 'error', message: jobRoleErrorMessage(err, 'Could not load this role.') });
      });
    return () => controller.abort();
  }, [roleId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);
  const setTab = (key: string) => setParams((prev) => { const next = new URLSearchParams(prev); if (key === 'overview') next.delete('tab'); else next.set('tab', key); return next; }, { replace: true });

  async function run(action: () => Promise<unknown>, success: string, failure: string) {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(jobRoleErrorMessage(err, failure));
    } finally {
      setBusy(false);
    }
  }

  async function activate(role: JobRoleDetail) {
    setBusy(true);
    setActivateError(null);
    try {
      await jobRoleGateway.activateRole(role.id, role.concurrencyVersion);
      setDialog(null);
      setNotice(`${role.title} is now active and can be assigned to employees.`);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      if (isConcurrencyConflict(err)) reload();
      setActivateError(isConcurrencyConflict(err) ? CONCURRENCY_CONFLICT_MESSAGE : jobRoleErrorMessage(err, 'Could not activate this role.'));
    } finally {
      setBusy(false);
    }
  }

  function wizardDone(role: JobRoleDetail, outcome: WizardOutcome, note?: string) {
    setDialog(null);
    setNotice(`${outcome === 'activated' ? `${role.title} is now active.` : `Draft saved for ${role.title}.`}${note ? ` ${note}` : ''}`);
    reload();
  }

  if (state.kind === 'loading') {
    return <section className="card card--wide jr-page"><p className="muted" role="status">Loading role{'…'}</p></section>;
  }
  if (state.kind === 'forbidden') {
    return (
      <section className="card card--wide jr-page">
        <p><Link to="/people/roles">{'←'} All roles</Link></p>
        <h2>Role</h2>
        <AccessDeniedNotice requiredPermissionLabel="permission to view roles (roles.read)" />
      </section>
    );
  }
  if (state.kind === 'notfound') {
    return (
      <section className="card card--wide jr-page">
        <p><Link to="/people/roles">{'←'} All roles</Link></p>
        <h2>Role not found</h2>
        <p className="muted">This role does not exist in your company, or it was removed from your view.</p>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="card card--wide jr-page">
        <p><Link to="/people/roles">{'←'} All roles</Link></p>
        <RetryableError message={state.message} onRetry={reload} />
      </section>
    );
  }

  const role = state.role;
  const content = normalizeJobRoleContent(role.content);
  const isDraftOnly = role.status === JobRoleStatus.Draft;
  const isActive = role.status === JobRoleStatus.Active;
  const isArchived = role.status === JobRoleStatus.Archived;
  const legacy = role.currentVersionNumber === null && !role.hasDraft;

  return (
    <section className="card card--wide jr-page">
      <p className="jr-back"><Link to="/people/roles">{'←'} All roles</Link></p>
      <div className="section-header">
        <div>
          <h2 ref={headingRef} tabIndex={-1}>{role.title} <RoleStatusBadge status={role.status} /></h2>
          <p className="muted jr-subtitle">
            {[role.roleCode, role.departmentName, role.jobFamily, role.careerLevel].filter(Boolean).join(' · ') || 'No code, department or family set'}
          </p>
        </div>
        {!readOnly && (
          <div className="jr-actions">
            {role.hasDraft && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('edit')} disabled={busy}>Edit draft</button>}
            {role.hasDraft && <button type="button" className="jr-btn jr-btn--primary" onClick={() => { setActivateError(null); setDialog('activate'); }} disabled={busy}>Activate</button>}
            {isActive && !role.hasDraft && (
              <button
                type="button" className="jr-btn jr-btn--secondary" disabled={busy}
                onClick={() => void run(async () => { await jobRoleGateway.createVersion(role.id, role.concurrencyVersion); }, 'A new draft version is open. Edit it, then activate it.', 'Could not create a new version.')}
              >
                Create New Version
              </button>
            )}
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('duplicate')} disabled={busy}>Duplicate</button>
            {isActive && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('assign')} disabled={busy}>Assign Employees</button>}
            {(isActive || isDraftOnly) && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('archive')} disabled={busy}>Archive</button>}
            {isArchived && role.currentVersionNumber !== null && (
              <button
                type="button" className="jr-btn jr-btn--secondary" disabled={busy}
                onClick={() => void run(async () => { await jobRoleGateway.restoreRole(role.id, role.concurrencyVersion); }, `${role.title} is active again.`, 'Could not restore this role.')}
              >
                Restore
              </button>
            )}
          </div>
        )}
      </div>

      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>
          You can view this role but not change it. Ask a company administrator if you need to edit, archive or assign roles.
        </p>
      )}
      {notice && <p className="jr-notice jr-notice--ok" role="status"><span aria-hidden="true">{'✓'} </span>{notice}</p>}
      {actionError && <p className="jr-error jr-error--block" role="alert">{actionError}</p>}
      {role.hasDraft && !isDraftOnly && (
        <p className="jr-notice" role="note">
          A draft version is open for this role. The description below is the current published version
          {role.currentVersionNumber !== null ? ` (version ${role.currentVersionNumber})` : ''}; employees keep it until the draft is activated.
        </p>
      )}
      {isArchived && (
        <p className="jr-notice" role="note">
          This role is archived. It keeps its history and any existing assignments, and it no longer appears when you add an employee.
        </p>
      )}

      <Tabs tabs={TABS} active={tab} onChange={setTab} idPrefix="role" label="Role sections">
        {tab === 'overview' && (
          <div className="jr-description">
            {legacy && (
              <p className="jr-notice" role="note">
                This role was created before the Job Role Library, so it has only a title and department. Create a new version to add a description.
              </p>
            )}
            <Facts
              items={[
                ['Internal code', role.roleCode],
                ['Department', role.departmentName],
                ['Job family', role.jobFamily],
                ['Level', role.careerLevel],
                ['Management level', role.managementLevel],
                ['SOC code', role.socCode],
                ['O*NET code', role.onetCode],
                ['Current version', role.currentVersionNumber !== null ? `Version ${role.currentVersionNumber}` : null],
                ['Effective since', role.effectiveFromUtc ? formatReportDate(role.effectiveFromUtc) : null],
                ['Started from', role.sourceTemplateId ? 'An EPN template (copied; the template is not linked)' : null],
              ]}
            />
            <section className="jr-block"><h4>Summary</h4>{content.summary ? <p>{content.summary}</p> : <p className="muted">Not provided</p>}</section>
            {content.alternateTitles.length > 0 && <section className="jr-block"><h4>Alternate titles</h4><ul>{content.alternateTitles.map((t) => <li key={t}>{t}</li>)}</ul></section>}
            <EmploymentGuidance content={content} />
          </div>
        )}
        {tab === 'responsibilities' && <ResponsibilitiesPanel content={content} />}
        {tab === 'qualifications' && <QualificationsPanel content={content} />}
        {tab === 'employees' && <AssignedEmployeesTab roleId={role.id} />}
        {tab === 'versions' && <VersionHistoryTab role={role} />}
        {tab === 'audit' && <AuditHistoryTab roleId={role.id} />}
        {tab === 'compensation' && <CompensationTab role={role} />}
      </Tabs>

      {dialog === 'edit' && (
        <RoleWizardDialog mode="edit" roleId={role.id} departments={departments} onClose={() => setDialog(null)} onDone={wizardDone} onChanged={reload} onForbidden={markReadOnly} />
      )}
      {dialog === 'duplicate' && (
        <DuplicateRoleDialog role={role} onClose={() => setDialog(null)} onForbidden={markReadOnly} onDuplicated={(created) => { setDialog(null); navigate(`/people/roles/${created.id}`); }} />
      )}
      {dialog === 'archive' && (
        <ArchiveRoleDialog
          role={role}
          onClose={() => setDialog(null)}
          onForbidden={markReadOnly}
          onArchived={() => { setDialog(null); setNotice(`${role.title} was archived. It was not deleted, and existing assignments are unchanged.`); reload(); }}
        />
      )}
      {dialog === 'assign' && (
        <AssignEmployeesDialog
          role={role}
          onClose={() => setDialog(null)}
          onForbidden={markReadOnly}
          onAssigned={(count) => { setNotice(`${count} ${count === 1 ? 'employee was' : 'employees were'} assigned to ${role.title}.`); reload(); }}
        />
      )}
      {dialog === 'activate' && (
        <ConfirmDialog title={`Activate ${role.title}?`} confirmLabel="Activate" busy={busy} error={activateError} onCancel={() => setDialog(null)} onConfirm={() => void activate(role)}>
          <p>Activating publishes the draft as the current version of this role.</p>
          <ul className="jr-plain-list">
            <li>The role becomes Active and can be assigned to employees.</li>
            <li>The previous published version is kept in the version history. Past assignments still point at the version that applied.</li>
            <li>Activating a role never changes anyone&apos;s pay or what they can do in EPN.</li>
          </ul>
        </ConfirmDialog>
      )}
    </section>
  );
}
